import {
    Component,
    ElementRef,
    Inject,
    OnInit,
    ViewChild,
} from '@angular/core';
import { ContainerService } from '../../../services/container/container.service';
import {
    Accessibility,
    Container,
    ContainerCreateRequest,
    ContainerType,
    Visibility,
} from '../../../models/container.types';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogConfig,
    MatDialogRef,
} from '@angular/material/dialog';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogData,
} from '../../confirmation-modal/confirmation-dialog.component';
import { FormControl } from '@angular/forms';
import { FormValidatorService } from '../../../services/form-validator.service';
import { delay, of, switchMap, tap } from 'rxjs';

export class ContainerSettingsDialogData {
    container: Container;
}

export const MAT_DIALOG_CONFIG_DATA: ConfirmationDialogData = {
    headerTranslationKey: 'containerSettings.deleteImageDialog.header',
    confirmationQuestion: {
        translationKey:
            'containerSettings.deleteImageDialog.confirmationQuestion',
        interpolateParameters: null,
    },
    confirmButtonTranslationKey: 'containerSettings.deleteImageDialog.confirm',
    rejectButtonTranslationKey: 'containerSettings.deleteImageDialog.reject',
    transferData: true,
};

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: [
        './settings.component.scss',
        '../container-administration-shared-styles.scss',
    ],
    standalone: false,
})
export class SettingsComponent implements OnInit {
    container: Container;

    selectedVisibility: Visibility;
    selectedAccessibility: Accessibility;
    availableVisibilityOptions: Visibility[] = [];
    availableAccessibilityOptions: Accessibility[] = [
        Accessibility.CLOSED,
        Accessibility.OPEN,
    ];

    containerNameControl: FormControl;
    containerDescriptionControl: FormControl;

    containerImage: string;
    showSpinner: boolean = false;

    matDialogConfig: MatDialogConfig<ConfirmationDialogData>;

    isCloseButtonHovered: boolean = false;
    isNameEditingActive: boolean = false;

    @ViewChild('containerNameInput') containerNameInputElement: ElementRef;
    @ViewChild('textArea') textAreaElement: ElementRef;

    constructor(
        private dialog: MatDialog,
        private containerService: ContainerService,
        private formValidatorService: FormValidatorService,
        public dialogRef: MatDialogRef<
            SettingsComponent,
            ContainerSettingsDialogData
        >,
        @Inject(MAT_DIALOG_DATA)
        public injectedData: ContainerSettingsDialogData
    ) {
        this.dialogRef.addPanelClass('container-administration-dialog');
        this.dialogRef.disableClose = true;
        this.dialogRef.backdropClick().subscribe(() => this.closeModal());
    }

    ngOnInit(): void {
        this.initialise(this.injectedData.container);
    }

    initialise(container: Container) {
        this.container = container;
        this.containerImage = container.containerImage;
        this.selectedVisibility = container.collaborationOptionsDto.visibility;
        this.selectedAccessibility =
            container.collaborationOptionsDto.accessibility;
        this.availableVisibilityOptions = container.organizationId
            ? [Visibility.PRIVATE, Visibility.GROUP, Visibility.ORG]
            : [Visibility.PRIVATE, Visibility.PUBLIC];
        this.container.collaborationOptionsDto = {
            visibility: this.selectedVisibility,
            accessibility: this.selectedAccessibility,
        };

        this.containerNameControl =
            this.formValidatorService.getControlByType('name');
        this.containerNameControl.setValue(this.container.name);
        this.containerDescriptionControl = new FormControl(
            this.container.description
        );

        this.matDialogConfig = new MatDialogConfig();
        this.matDialogConfig.data = MAT_DIALOG_CONFIG_DATA;
    }

    closeModal() {
        this.dialogRef.close();
    }

    activateNameEditing() {
        this.isNameEditingActive = true;
        setTimeout(() => {
            this.containerNameInputElement.nativeElement.focus();
        }, 10);
    }

    deactivateNameEditing() {
        this.isNameEditingActive = false;
    }

    saveNameChange() {
        this.containerNameControl.setValue(
            this.containerNameControl.value.trim()
        );

        if (this.containerNameControl.invalid) {
            this.containerNameInputElement.nativeElement.focus();
            return;
        }

        this.deactivateNameEditing();

        if (this.containerNameControl.value !== this.container.name) {
            this.container.name = this.containerNameControl.value;
            this.updateContainer();
        }
    }

    discardNameChange() {
        this.containerNameControl.setValue(this.container.name);
        this.deactivateNameEditing();
    }

    getTranslationPrefixByContainerType(): 'imgDb' | 'project' {
        return this.container?.containerType === ContainerType.IMAGE_DB
            ? 'imgDb'
            : 'project';
    }

    onVisibilityChange(option: Visibility) {
        if (this.selectedVisibility !== option) {
            this.selectedVisibility = option;
            this.container.collaborationOptionsDto.visibility = option;
            this.updateContainer();
        }
    }

    onAccessibilityChange(option: Accessibility) {
        if (this.selectedAccessibility !== option) {
            this.selectedAccessibility = option;
            this.container.collaborationOptionsDto.accessibility = option;
            this.updateContainer();
        }
    }

    saveTextAreaChanges() {
        this.containerDescriptionControl.setValue(
            this.containerDescriptionControl.value.trim()
        );
        if (
            this.container.description !==
            this.containerDescriptionControl.value
        ) {
            this.container.description = this.containerDescriptionControl.value;
            this.updateContainer();
        }
    }

    discardTextAreaChanges() {
        this.containerDescriptionControl.setValue(this.container.description);
        this.textAreaElement.nativeElement.blur();
    }

    updateContainer() {
        const dto = this.createDto();
        this.containerService.update(this.container.id, dto).subscribe();
    }

    createDto(): ContainerCreateRequest {
        return {
            collaborationOptionsDto: this.container.collaborationOptionsDto,
            description: this.container.description,
            name: this.container.name,
        } as ContainerCreateRequest;
    }

    saveContainerImage(image: File) {
        this.showSpinner = true;
        this.containerImage = null;

        this.containerService
            .saveContainerImage(this.container.id, image)
            .pipe(
                delay(500),
                tap((response) => {
                    this.containerImage = response;
                    this.showSpinner = false;
                })
            )
            .subscribe();
    }

    deleteContainerImage() {
        this.dialog
            .open(ConfirmationDialogComponent, this.matDialogConfig)
            .afterClosed()
            .pipe(
                switchMap((res) => {
                    if (res) {
                        this.containerImage = null;
                        return this.containerService.deleteContainerImage(
                            this.container.id
                        );
                    }
                    return of(null);
                })
            )
            .subscribe();
    }
}
